import React, { useState, useEffect } from 'react';
import { useDraft } from '../contexts/DraftContext';
import { useAuth } from '../../../contexts/AuthContext';
import styled from 'styled-components';
import { 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  CircularProgress,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  Divider,
  Tooltip,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileSystemService from '../../../services/FileSystemService';
import { auth } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import MergeReplaceDialog from './MergeReplaceDialog';

const DraftsList = styled(Paper)`
  margin: 0;
  background-color: transparent;
  box-shadow: none;
  display: flex;
  flex-direction: column;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const ErrorMessage = styled(Box)`
  color: #f44336;
  text-align: center;
  padding: 20px;
`;

const EmptyState = styled(Box)`
  color: #666;
  text-align: center;
  padding: 20px;
  font-style: italic;
`;

const ActionButtons = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 12px 16px 16px 16px;
  flex-wrap: wrap;
  padding: 0 8px;
  box-sizing: border-box;
  align-items: center;

  @media (max-width: 768px) {
    gap: 8px;
    margin: 10px 12px 12px 12px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    margin: 6px 6px 10px 6px;
    flex-direction: column;
    padding: 0 4px;

    button {
      width: 100%;
      min-width: unset;
      font-size: 12px;
      padding: 6px 12px;
    }
  }

  @media (max-width: 440px) {
    gap: 4px;
    margin: 4px 4px 8px 4px;
    padding: 0 2px;

    button {
      font-size: 11px;
      padding: 5px 10px;
    }
  }
`;

const TabDescription = styled(Typography)`
  text-align: center;
  color: #999;
  margin: 10px 0;
  font-size: 0.9rem;
  padding: 0 20px;
`;

const ScrollableListContainer = styled(Box)`
  flex: 1;
  min-height: 0;
`;

const SearchContainer = styled(Box)`
  display: flex;
  gap: 12px;
  padding: 0 16px 12px 16px;
  flex-wrap: wrap;
`;

const DropZone = styled(Box)`
  border: 1px solid ${props => props.$isDragOver ? '#2196f3' : 'transparent'};
  border-radius: 4px;
  padding: 12px 16px;
  text-align: center;
  background-color: ${props => props.$isDragOver ? 'rgba(33, 150, 243, 0.05)' : 'transparent'};
  transition: all 0.2s ease;
  margin: 8px 16px 0 16px;

  &:hover {
    border-color: rgba(33, 150, 243, 0.3);
    background-color: rgba(33, 150, 243, 0.02);
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    margin: 6px 12px 0 12px;
  }

  @media (max-width: 480px) {
    padding: 6px 8px;
    margin: 4px 6px 0 6px;
  }

  @media (max-width: 440px) {
    padding: 4px 6px;
    margin: 3px 4px 0 4px;
  }
`;

const DropZoneContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const DropZoneIcon = styled(CloudUploadIcon)`
  font-size: 24px;
  color: ${props => props.$isDragOver ? '#2196f3' : '#666'};
  transition: color 0.2s ease;
`;

const DropZoneText = styled(Typography)`
  color: ${props => props.$isDragOver ? '#2196f3' : '#888'};
  transition: color 0.2s ease;
  font-size: 12px;

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 440px) {
    font-size: 10px;
  }
`;

const SubtleImportButton = styled(Button)`
  background-color: transparent;
  border: 1px solid #444;
  color: #aaa;
  
  &:hover {
    background-color: rgba(33, 150, 243, 0.1);
    border-color: #2196f3;
    color: #2196f3;
  }
`;

const TopActionsContainer = styled(Box)`
  display: flex;
  gap: 16px;
  margin: 16px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin: 12px;
  }
`;

const MatchDataSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const ImportSection = styled(Paper)`
  flex: 1;
  min-width: 0;
  background-color: #1a1a1a;
  display: flex;
  flex-direction: column;
  padding: 8px 0;

  @media (min-width: 769px) {
    display: flex; /* Show on desktop */
  }

  @media (max-width: 768px) {
    display: flex; /* Show on mobile to replace the input boxes */
    padding: 6px 0;
  }
`;

const SavedDrafts = ({ onMatchDataPulled, onDraftLoaded }) => {
  const {
    getDrafts,
    loadDraft,
    deleteDraft,
    importDraft,
    importDraftData,
    mergeDraftData,
    saveDraft,
    isFileSystemApiSupported,
    currentDraftId
  } = useDraft();

  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [tournamentSearch, setTournamentSearch] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showMergeReplaceDialog, setShowMergeReplaceDialog] = useState(false);
  const [pendingFileImport, setPendingFileImport] = useState(null);
  
  const loadSavedDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await getDrafts();
      setSavedDrafts(drafts || []);
    } catch (error) {
      console.error('Error loading drafts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load drafts on component mount
  useEffect(() => {
    loadSavedDrafts();
  }, []);

  // Prevent default drag behavior on the page
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);
  
  // Handle loading a draft
  const handleLoadDraft = async (draftId, draftFilename) => {
    try {
      setLoading(true);
      // Pass both the ID and filename to loadDraft
      const result = await loadDraft(draftId, draftFilename);
      
      // Check if confirmation is needed
      if (result && result.needsConfirmation) {
        setPendingAction({
          type: 'loadDraft',
          draftId: result.draftId,
          filename: result.filename
        });
        setShowConfirmation(true);
        setLoading(false);
        return;
      }
      // Close sidebar after successful load
      if (onDraftLoaded) onDraftLoaded();
    } catch (error) {
      console.error('Error loading draft:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle deleting a draft
  const handleDeleteDraft = async (draftId, event) => {
    event.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this draft?')) {
      try {
        setLoading(true);
        await deleteDraft(draftId);
        await loadSavedDrafts(); // Refresh the list
      } catch (error) {
        console.error('Error deleting draft:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle importing a draft via file picker
  const handleImportDraft = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use FileSystemService to open file picker and load draft
      const FileSystemService = (await import('../../../services/FileSystemService')).default;
      const draftData = await FileSystemService.loadDraftFile();

      if (!draftData) {
        setLoading(false);
        return; // User cancelled
      }

      // Generate an ID if none exists
      if (!draftData.id) {
        draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Add missing fields if necessary
      draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
      draftData.updatedAt = draftData.updatedAt || draftData.createdAt;

      // Create a fake file object for tracking
      const fileName = draftData.teamName ? `${draftData.teamName}_${draftData.tournament || 'draft'}.json` : 'imported_draft.json';
      const fakeFile = { name: fileName };

      // If it's a new draft (no currentDraftId), just replace without asking
      if (!currentDraftId) {
        // Check if user is authenticated
        if (!currentUser) {
          // Store draft data for loading after login
          sessionStorage.setItem('pendingDraftLoad', JSON.stringify(draftData));
          navigate('/login');
          setLoading(false);
          return;
        }
        importDraftData(draftData);
        navigate('/drafts');
        setLoading(false);
      } else {
        // Store the pending import and show merge/replace dialog
        setPendingFileImport({ file: fakeFile, draftData });
        setShowMergeReplaceDialog(true);
        setLoading(false);
      }

    } catch (error) {
      console.error('Error importing draft:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Handle file drop
  const handleFileDrop = async (files) => {
    console.log('handleFileDrop called with:', files.length, 'files');
    if (files.length === 0) return;

    const file = files[0];
    console.log('Processing file:', file.name, 'Type:', file.type);

    // Check if it's a JSON file
    if (!file.name.toLowerCase().endsWith('.json')) {
      console.warn('Invalid file type. Expected JSON, got:', file.name);
      setError('Please drop a JSON file');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Reading file content...');

      // Read the file content
      const text = await file.text();
      console.log('File content length:', text.length);
      let draftData;

      try {
        draftData = JSON.parse(text);
        console.log('JSON parsed successfully, draft data:', draftData);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        setError('Invalid JSON file format');
        setLoading(false);
        return;
      }

      // Validate that it's a draft file
      if (!draftData || (!draftData.id && !draftData.scenariosFirstPick && !draftData.scenariosSecondPick)) {
        console.warn('Invalid draft structure:', draftData);
        setError('This doesn\'t appear to be a valid draft file');
        setLoading(false);
        return;
      }

      console.log('Draft validation passed');

      // Generate an ID if none exists
      if (!draftData.id) {
        draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Add missing fields if necessary
      draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
      draftData.updatedAt = draftData.updatedAt || draftData.createdAt;

      // If it's a new draft (no currentDraftId), just replace without asking
      if (!currentDraftId) {
        // Check if user is authenticated
        if (!currentUser) {
          // Store draft data for loading after login
          sessionStorage.setItem('pendingDraftLoad', JSON.stringify(draftData));
          navigate('/login');
          setLoading(false);
          return;
        }
        importDraftData(draftData);
        navigate('/drafts');
        setLoading(false);
      } else {
        // Store the pending import and show merge/replace dialog
        setPendingFileImport({ file, draftData });
        setShowMergeReplaceDialog(true);
        setLoading(false);
      }

    } catch (err) {
      console.error('Error importing dropped file:', err);
      setError('Failed to import draft: ' + err.message);
      setLoading(false);
    }
  };

  // Drag and drop event handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide overlay if we're leaving the drop zone container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      console.log('Files dropped:', files.map(f => f.name));
      handleFileDrop(files);
    }
  };

  // Handle click on drop zone
  const handleDropZoneClick = () => {
    handleImportDraft();
  };

  // Merge/Replace dialog handlers
  const handleMerge = async () => {
    if (!pendingFileImport) return;

    try {
      setLoading(true);
      const { file, draftData } = pendingFileImport;

      // Merge the imported data with current draft
      mergeDraftData(draftData);

      // Close dialog and clear pending import
      setShowMergeReplaceDialog(false);
      setPendingFileImport(null);

      // Show success message
      console.log('Successfully merged draft data');
      setLoading(false);
    } catch (err) {
      console.error('Error merging draft:', err);
      setError('Failed to merge draft: ' + err.message);
      setLoading(false);
    }
  };

  const handleReplace = async () => {
    if (!pendingFileImport) return;

    try {
      setLoading(true);
      const { file, draftData } = pendingFileImport;

      // Use the importDraftData function to replace current draft
      const importResult = await importDraftData(draftData, true); // skipConfirmation = true
      console.log('Import result:', importResult);

      if (importResult && importResult.id) {
        // Track the saved draft for future reference
        const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');

        // Generate display title that matches filename format
        const cleanString = (str) => {
          return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
        };

        const teamName = cleanString(draftData.teamName) || 'Team';
        const tournamentName = cleanString(draftData.tournament) || 'Tournament';
        const stageName = cleanString(draftData.stage) || 'Stage';
        const dateString = draftData.date || new Date().toISOString().slice(0, 10);

        const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

        tracking[draftData.id] = {
          id: draftData.id,
          filename: file.name,
          title: displayTitle,
          tournament: draftData.tournament || '',
          stage: draftData.stage || '',
          date: draftData.date || draftData.createdAt,
          updatedAt: draftData.updatedAt,
          createdAt: draftData.createdAt,
          scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
          scenariosSecondPick: draftData.scenariosSecondPick ? true : false
        };

        localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

        // Cache the draft data for future quick loading
        try {
          localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
          console.log('Draft cached from drop:', draftData.id);
        } catch (cacheError) {
          console.warn('Could not cache draft data', cacheError);
        }

        console.log('Draft replaced successfully:', draftData.id);

        // Close dialog and clear pending import
        setShowMergeReplaceDialog(false);
        setPendingFileImport(null);

        // Stay on current page - draft is already loaded into context
        // No need to navigate since we're already on /drafts
      }
    } catch (err) {
      console.error('Error replacing draft:', err);
      setError('Failed to replace draft: ' + err.message);
      setLoading(false);
    }
  };

  const handleMergeReplaceCancel = () => {
    setShowMergeReplaceDialog(false);
    setPendingFileImport(null);
  };

  // Confirmation dialog handlers
  const handleConfirmSave = async () => {
    try {
      // Save current draft first
      await saveDraft();
      // Then execute the pending action
      await executePendingAction(true);
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmDiscard = async () => {
    try {
      await executePendingAction(true);
    } catch (error) {
      console.error('Error executing action:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingAction(null);
  };

  // Execute the pending action
  const executePendingAction = async (skipConfirmation = false) => {
    if (!pendingAction) return;

    try {
      setLoading(true);
      switch (pendingAction.type) {
        case 'loadDraft':
          await loadDraft(pendingAction.draftId, pendingAction.filename, skipConfirmation);
          break;
        case 'importDraft':
          await importDraft(skipConfirmation);
          await loadSavedDrafts();
          break;
        case 'importFile':
          const importResult = await importDraftData(pendingAction.data, skipConfirmation);
          if (importResult && importResult.id) {
            // Handle successful import (tracking, caching, navigation)
            const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');
            const draftData = pendingAction.data;
            const file = pendingAction.file;

            const cleanString = (str) => {
              return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
            };

            const teamName = cleanString(draftData.teamName) || 'Team';
            const tournamentName = cleanString(draftData.tournament) || 'Tournament';
            const stageName = cleanString(draftData.stage) || 'Stage';
            const dateString = draftData.date || new Date().toISOString().slice(0, 10);

            const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

            tracking[draftData.id] = {
              id: draftData.id,
              filename: file.name,
              title: displayTitle,
              tournament: draftData.tournament || '',
              stage: draftData.stage || '',
              date: draftData.date || draftData.createdAt,
              updatedAt: draftData.updatedAt,
              createdAt: draftData.createdAt,
              scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
              scenariosSecondPick: draftData.scenariosSecondPick ? true : false
            };

            localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

            try {
              localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
              console.log('Draft cached from drop:', draftData.id);
            } catch (cacheError) {
              console.warn('Could not cache draft data', cacheError);
            }

            // Stay on current page - draft is already loaded into context
            console.log('Draft imported successfully:', draftData.id);
          }
          break;
        default:
          console.warn('Unknown pending action type:', pendingAction.type);
      }
    } catch (error) {
      console.error('Error executing pending action:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Filter drafts based on search terms
  const filteredDrafts = savedDrafts.filter(draft => {
    const teamMatches = !teamSearch || 
      (draft.title && draft.title.toLowerCase().includes(teamSearch.toLowerCase())) ||
      (draft.tournament && draft.tournament.toLowerCase().includes(teamSearch.toLowerCase()));
    
    const tournamentMatches = !tournamentSearch || 
      (draft.tournament && draft.tournament.toLowerCase().includes(tournamentSearch.toLowerCase())) ||
      (draft.title && draft.title.toLowerCase().includes(tournamentSearch.toLowerCase()));
    
    return teamMatches && tournamentMatches;
  });
  
  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  if (loading) {
    return (
      <DraftsList>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </DraftsList>
    );
  }

  return (
    <>
      <DraftsList>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mx: 2, my: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <ActionButtons sx={{ mt: 1, mb: 0 }}>
        <SubtleImportButton
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={handleImportDraft}
          size="small"
        >
          Browse Files
        </SubtleImportButton>

        <Tooltip title="Refresh drafts list">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={loadSavedDrafts}
            size="small"
          >
            Refresh
          </Button>
        </Tooltip>
      </ActionButtons>

      <SearchContainer>
        <TextField
          label="Search by Team"
          variant="outlined"
          size="small"
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          placeholder="Enter team name..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999' }} />
              </InputAdornment>
            ),
            endAdornment: teamSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setTeamSearch('')}
                  sx={{ color: '#999' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: '200px',
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: '#666',
              },
              '&:hover fieldset': {
                borderColor: '#888',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976D2',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#999',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1976D2',
            },
          }}
        />
        
        <TextField
          label="Search by Tournament"
          variant="outlined"
          size="small"
          value={tournamentSearch}
          onChange={(e) => setTournamentSearch(e.target.value)}
          placeholder="Enter tournament name..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999' }} />
              </InputAdornment>
            ),
            endAdornment: tournamentSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setTournamentSearch('')}
                  sx={{ color: '#999' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: '200px',
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: '#666',
              },
              '&:hover fieldset': {
                borderColor: '#888',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976D2',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#999',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1976D2',
            },
          }}
        />
      </SearchContainer>

      {(teamSearch || tournamentSearch) && (
        <Box sx={{ textAlign: 'center', pb: 1, color: '#999', fontSize: '0.9rem' }}>
          Showing {filteredDrafts.length} of {savedDrafts.length} drafts
        </Box>
      )}

      <ScrollableListContainer>
      {(!savedDrafts || savedDrafts.length === 0) ? (
        <EmptyState>
          <Typography variant="body1" color="textSecondary">
            No saved drafts found in tracking
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Import a draft or save one first
          </Typography>
        </EmptyState>
        ) : filteredDrafts.length === 0 ? (
          <EmptyState>
            <Typography variant="body1" color="textSecondary">
              No drafts match your search criteria
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Try adjusting your search terms
            </Typography>
          </EmptyState>
      ) : (
        <List>
            {filteredDrafts.map((draft) => (
            <ListItem
              key={draft.id}
              onClick={() => handleLoadDraft(draft.id, draft.filename)}
              sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                },
                '& .MuiListItemText-primary': {
                  color: '#61dafb'
                },
                '& .MuiListItemText-secondary': {
                  color: '#999'
                }
              }}
              secondaryAction={
                <IconButton 
                  edge="end" 
                  aria-label="delete"
                  onClick={(e) => handleDeleteDraft(draft.id, e)}
                  sx={{ color: '#999' }}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <span style={{ color: '#61dafb', fontSize: '1.1rem', fontWeight: '500' }}>
                    {draft.title || 'Untitled Draft'} 
                    {draft.tournament && ` - ${draft.tournament}`}
                  </span>
                }
                secondary={
                  <div>
                    {draft.filename && (
                      <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '2px' }}>
                        {draft.filename}
                      </div>
                    )}
                    <div style={{ color: '#999', fontSize: '0.875rem' }}>
                      Created: {formatDate(draft.createdAt)}
                      {draft.updatedAt && draft.updatedAt !== draft.createdAt && 
                        ` • Updated: ${formatDate(draft.updatedAt)}`}
                    </div>
                  </div>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      </ScrollableListContainer>
    </DraftsList>

    {/* Confirmation dialog for unsaved changes */}
    <ConfirmationDialog
      isOpen={showConfirmation}
      title="Unsaved Changes"
      message="You have unsaved changes. Would you like to save them before continuing?"
      onSave={handleConfirmSave}
      onDiscard={handleConfirmDiscard}
      onCancel={handleConfirmCancel}
      saveButtonText="Save & Continue"
      discardButtonText="Discard Changes"
      cancelButtonText="Cancel"
    />

    {/* Merge/Replace dialog for file imports */}
    <MergeReplaceDialog
      open={showMergeReplaceDialog}
      onClose={handleMergeReplaceCancel}
      onMerge={handleMerge}
      onReplace={handleReplace}
      fileName={pendingFileImport?.file?.name || 'draft.json'}
    />
  </>
  );
};

export default SavedDrafts; 